import {
  AvatarGroup,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  Backdrop,
  Badge,
} from "@mui/material";
import React, { useState } from "react";
import dayjs from "dayjs";
import AvatarRem from "./AvatarRem";
import { TaskCardProps } from "model/activitiesModel";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Tooltip from "@mui/material/Tooltip";
import InsertCommentOutlinedIcon from "@mui/icons-material/InsertCommentRounded";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltRounded";
import FolderOutlinedIcon from "@mui/icons-material/FolderRounded";
import { getTranslation } from "common";
import { useTranslations, useUser } from "hooks";
import Labels from "./labels/Labels";
import {
  getLabelDataFromJsonTags,
  getOffsetHeightForTask,
  getParticipants,
  getInitials,
  getDisplayName,
  getUserData,
  getUserDataValue,
  LABEL_OFFSET,
  TASK_CARD_HEIGHT,
  // getOffsetHeightForParticipants,
  Participant,
  DESCRIPTION_OFFSET,
  doesDescriptionNeedExtraHeight,
} from "common/utilities";
import { baseApi } from "api";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { UserData } from "hooks/useUserDataKeys";
import HodDialogs from "./dialogs/HodDialogs";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useNavigate } from "react-router-dom";
import TaskQuickSwapButtons from "./TaskQuickSwapButtons";
import SequenceFlowsDialog from "./dialogs/SequenceFlowsDialog";
// SLA in ToDo (F3c): badge della riga, alimentato dal contesto della superficie
// SLA montata da `src/pages/MyActivities.tsx` (piano sez. 3 riga "Attivita'").
import SlaTaskSlaBadge from "./sla/SlaTaskSlaBadge";

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  labelCompleteMode,
  setDetail,
  onAssignClick,
  view,
  handleNextStep: onNextStep,
  handleBackStep,
  handleSequenceFlowChoice,
}) => {
  const translations = useTranslations();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const loggedInUser = useUser();

  const [groupUsers, setGroupUsers] = useState<
    {
      id: number;
      name: string;
      firstname: string;
      lastname: string;
      picture: string;
    }[]
  >([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [openSequenceDialog, setOpenSequenceDialog] = useState(false);

  const handleNextStep = (task: TaskCardProps["task"]) => {
    if (
      task.state === 1 &&
      task.sequenceflows &&
      task.sequenceflows.length > 1 &&
      handleSequenceFlowChoice
    ) {
      setOpenSequenceDialog(true);
      return;
    }

    if (task.state === 1 && task.sequenceflows?.length === 1 && handleSequenceFlowChoice) {
      handleSequenceFlowChoice(task, task.sequenceflows[0]);
      return;
    }

    onNextStep?.(task);
  };
  const canHandleNextStep = Boolean(onNextStep || handleSequenceFlowChoice);

  const handleAssignClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDialogOpen(true);
    setLoadingUsers(true);

    try {
      const response = await baseApi.get(
        `/tasks/processtoken/${task.idprocesstoken}/users`
      );
      setGroupUsers(response.data.users);
    } catch {
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserSelect = (user: {
    id: number;
    firstname: string;
    lastname: string;
  }) => {
    setSelectedUser({
      id: user.id,
      name: `${user.firstname} ${user.lastname}`,
    });
    setDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAssign = async () => {
    if (selectedUser) {
      try {
        await baseApi.put(
          `/tasks/processtoken/${task.idprocesstoken}/assignment/user/${selectedUser.id}`,
          {
            iduser: selectedUser.id,
          }
        );
      } catch {
      } finally {
        setConfirmDialogOpen(false);
        setSelectedUser(null);
      }
    }
  };

  const icons = [
    {
      title: getTranslation(translations, "settings.comments.label"),
      IconComponent: InsertCommentOutlinedIcon,
    },
    {
      title: getTranslation(translations, "generic.fields.caption"),
      IconComponent: ListAltOutlinedIcon,
    },
    {
      title: getTranslation(translations, "generic.attached_files.caption"),
      IconComponent: FolderOutlinedIcon,
    },
  ];

  const onButtonClickIcon = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setDetail(task?.idprocesstoken);
  };

  // const typographyStyleDescription = {
  //   fontSize: 16,
  //   fontWeight: 700,
  //   whiteSpace: "nowrap",
  //   overflow: "hidden",
  // };

  const chipStylePriority = (theme: any) => ({
    "& .MuiChip-label": {
      px: 0,
    },
    backgroundColor:
      theme.palette.mode === "dark" ? `${theme.palette.primary.main}24` : "transparent",
    fontSize: 14,
    fontWeight: "bold",
    minWidth: 45,
    px: 1,
    borderRadius: "6px",
    border: `1.5px solid ${theme.palette.primary.main}`,
    height: 20,
    color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.text.primary,
  });

  const getDateChipSx = (theme: any, compact = false) => ({
    "& .MuiChip-label": {
      px: compact ? 0.5 : 0,
    },
    fontSize: compact ? 11 : 13,
    width: compact ? undefined : 40,
    borderRadius: 1,
    marginRight: compact ? undefined : 1,
    height: compact ? 18 : 20,
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.background.paper,
  });

  const getCardHeight = () => {
    if (view === "minimal") {
      return 60;
    }
    if (view === "compact") {
      // Tall enough to fit: 2-line clamped title + process row +
      // footer row with badge icons, without clipping the badge
      // counters at the bottom edge.
      return 110 + (doesDescriptionNeedExtraHeight(task.description) ? DESCRIPTION_OFFSET : 0);
    }
    return (
      TASK_CARD_HEIGHT +
      (labelCompleteMode ? LABEL_OFFSET : 0) +
      getOffsetHeightForTask(task.userdataLength) +
      // getOffsetHeightForParticipants(getParticipants(task.jsonparticipant), task.state === 1) +
      (doesDescriptionNeedExtraHeight(task.description) ? DESCRIPTION_OFFSET : 0)
    );
  };

  const getCardPadding = () => {
    if (view === "minimal") return "8px";
    if (view === "compact") return "8px";
    return "10px";
  };

  const navigate = useNavigate();

  return (
    <React.Fragment>
      <Card
        sx={{
          position: "relative",
          height: getCardHeight(),
          flex: 1,
          padding: getCardPadding(),
          width: "calc(100% - 32px)",
          borderRadius: view === "minimal" ? "12px" : "16px",
          overflowx: "hidden",
          display: "flex",
          cursor: "grab",
        }}
        variant="elevation"
        elevation={3}
      >
        <Backdrop
          open={!!task.loading}
          component="div"
          sx={{
            position: "absolute",
            zIndex: 9999,
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Backdrop>
        <Stack sx={{ maxWidth: "100%", flex: 1 }}>
          <Stack
            flex={1}
            direction="row"
            sx={{ position: "relative", maxWidth: "100%" }}
          >
            <CardContent
              component={Stack}
              direction="column"
              flex={1}
              sx={{ maxWidth: "100%" }}
              p={"0px!important"}
            >
              {/* SLA visibile **gia' in ToDo** (piano F3c sez. 5): il badge
                  `compact` (un chip con i dettagli nel tooltip) e' montato
                  dentro una riga gia' esistente di ogni vista, perche' la card
                  ha altezza fissa e una riga in piu' tagliava il contenuto.
                  Senza SLA non rende nulla. */}
              {/* MINIMAL VIEW */}
              {view === "minimal" && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  height="100%"
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      fontSize: 14,
                    }}
                  >
                    {task?.description}
                  </Typography>
                  <Box sx={{ ml: 1, display: "flex", minWidth: 0 }}>
                    <SlaTaskSlaBadge task={task} variant="compact" />
                  </Box>
                  {task.assignmentmode === 2 && task.iduser === 0 && (
                    <Tooltip
                      title={getTranslation(
                        translations,
                        "generic.assign_task_to_user"
                      )}
                    >
                      <IconButton
                        id="hod-dialog-button"
                        size="small"
                        onClick={handleAssignClick}
                      >
                        <PersonAddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    sx={(theme) => ({
                      border: `1px solid ${theme.palette.grey[200]}`,
                      borderRadius: "5px",
                      padding: "2px",
                      ml: 1,
                    })}
                    className="detail-btn"
                    id={`task-detail-btn-${task?.idprocesstoken}`}
                    disabled={task?.loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetail(task?.idprocesstoken);
                    }}
                  >
                    <MoreHorizIcon fontSize="small" />
                  </IconButton>
                  {/* <TaskQuickSwapButtons
                    task={task}
                    onBack={handleBackStep}
                    onNext={handleNextStep}
                  /> */}
                </Box>
              )}

              {/* COMPACT VIEW */}
              {view === "compact" && (
                <Box display="flex" flexDirection="column" height="100%">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={0.5}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        flex: 1,
                        fontSize: 14,
                        wordBreak: "break-word",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {task?.description}
                    </Typography>
                    <IconButton
                      sx={(theme) => ({
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: "5px",
                        padding: "2px",
                        ml: 1,
                      })}
                      className="detail-btn"
                      id={`task-detail-btn-${task?.idprocesstoken}`}
                      disabled={task?.loading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetail(task?.idprocesstoken);
                      }}
                    >
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                    <Tooltip title={getTranslation(translations, "pm.label.caption")} arrow placement="top">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/dashboard/process/edit/${task.idprocessmodel}`, { state: { designerid: task.designerid } });
                        }}
                        sx={{ mr: 1 }}
                      >
                        <AccountTreeIcon sx={{ color: "primary.main", ":hover": { color: "secondary.main" }, ml: -1 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "text.secondary",
                        fontSize: 12,
                        mb: 0.5,
                      }}
                    >
                      {task?.processmodelname}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt="auto"
                  >
                    <Box sx={{ display: "flex", minWidth: 0 }}>
                      <SlaTaskSlaBadge task={task} variant="compact" />
                    </Box>
                    <Box display="flex" gap={2} marginLeft="auto" marginRight={2}>
                      {task.assignmentmode === 2 && task.iduser === 0 && (
                        <Tooltip
                          title={getTranslation(
                            translations,
                            "generic.assign_task_to_user"
                          )}
                        >
                          <IconButton
                            id="hod-dialog-button"
                            size="small"
                            onClick={handleAssignClick}
                          >
                            <PersonAddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {icons.map((tooltip, index) => {
                        const count = [
                          task.countcomments,
                          task.countuserdatafields,
                          task.countattachments,
                        ][index];
                        return count > 0 ? (
                          <Tooltip
                            key={index}
                            title={tooltip.title}
                            arrow
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              id={`task-detail-btn-${tooltip.title}`}
                              onClick={onButtonClickIcon}
                              sx={{ p: 0.25 }}
                            >
                              <Badge
                                badgeContent={count}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: 9,
                                    height: 14,
                                    minWidth: 14,
                                  },
                                }}
                              >
                                <tooltip.IconComponent sx={{ fontSize: 18 }} />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        ) : null;
                      })}
                    </Box>
                    <Chip
                      sx={(theme) => getDateChipSx(theme, true)}
                      label={dayjs(task?.created).format("DD/MM")}
                    />
                    {/* <TaskQuickSwapButtons
                      task={task}
                      onBack={handleBackStep}
                      onNext={handleNextStep}
                    /> */}
                  </Box>
                </Box>
              )}

              {/* EXPANDED VIEW (stile originale) */}
              {view === "extended" && (
                <>
                  <Box height={10 + (labelCompleteMode ? LABEL_OFFSET : 0)}>
                    <Labels
                      labelCompleteMode={labelCompleteMode}
                      inCard
                      labels={getLabelDataFromJsonTags(task?.jsontag)}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flex: 1,
                      height: "100%",
                      maxWidth: "100%",
                    }}
                  >
                    <Stack
                      sx={{
                        maxWidth: "100%",
                        alignSelf: "flex-start",
                        flex: 1,
                        p: 0,
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="start"
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          sx={{ maxWidth: "60%" }}
                          alignItems="flex-start"
                        >
                          {Object.entries<UserData>(
                            getUserData(task?.userdata)
                          ).map(([key, value]) => {
                            let displayValue = `${key}: ${getUserDataValue(
                              value
                            )}`;

                            return (
                              <React.Fragment key={key}>
                                {value.inCard ? (
                                  <Box
                                    display="flex"
                                    sx={{
                                      maxWidth: "100%",
                                      overflow: "hidden",
                                      textWrap: "nowrap",
                                    }}
                                    alignItems="center"
                                  >
                                    <Tooltip
                                      title={displayValue}
                                      placement="bottom-start"
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        color="text.disabled"
                                        sx={{
                                          fontSize: 12,
                                          overflow: "hidden",
                                          fontWeight: "bold",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {displayValue}
                                      </Typography>
                                    </Tooltip>
                                  </Box>
                                ) : null}
                              </React.Fragment>
                            );
                          })}
                        </Box>
                        <Box
                          sx={{
                            width: "40%",
                            display: "flex",
                            justifyContent: "end",
                            alignItems: "center",
                          }}
                        >
                          <Chip
                            sx={(theme) => getDateChipSx(theme)}
                            label={dayjs(task?.created).format("DD/MM")}
                          />
                          <IconButton
                            sx={(theme) => ({
                              border: `1px solid ${theme.palette.grey[200]}`,
                              borderRadius: "5px",
                              padding: "2px",
                            })}
                            className="detail-btn"
                            id={`task-detail-btn-${task?.idprocesstoken}`}
                            aria-label={getTranslation(
                              translations,
                              "generic.add_box.caption"
                            )}
                            disabled={task?.loading}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDetail(task?.idprocesstoken);
                            }}
                          >
                            <MoreHorizIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box pt={1}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          {task?.flownodename &&
                            (task?.flownodename.length > 25 ? (
                              <Tooltip
                                title={task?.flownodename}
                                placement="bottom-start"
                              >
                                <Typography sx={{ paddingTop: "1px" }}>
                                  {`${task?.flownodename.substring(0, 25).toLocaleLowerCase()}...`}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography sx={{ paddingTop: "1px" }} variant="caption">
                                {task?.flownodename}
                              </Typography>
                            ))}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              ml: "auto",
                              minWidth: 0,
                            }}
                          >
                          <SlaTaskSlaBadge task={task} variant="compact" />
                          {task?.priority_name &&
                            (task?.priority_name.length > 6 ? (
                              <Tooltip
                                title={task?.priority_name}
                                placement="bottom-start"
                              >
                                <Chip
                                  sx={chipStylePriority}
                                  label={`${task?.priority_name.substring(
                                    0,
                                    6
                                  )}...`}
                                />
                              </Tooltip>
                            ) : (
                              <Chip
                                sx={chipStylePriority}
                                label={task?.priority_name}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                          <Tooltip title={getTranslation(translations, "pm.label.caption")} arrow placement="top">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/process/edit/${task.idprocessmodel}`, { state: { designerid: task.designerid } });
                              }}
                              sx={{ mr: 1 }}
                            >
                              <AccountTreeIcon sx={{ fontSize: 18, color: "primary.main", ":hover": { color: "secondary.main" }, ml: -1 }} />
                            </IconButton>
                          </Tooltip>
                          <Typography
                            sx={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              width: "100%",
                              WebkitBoxOrient: "vertical",
                              textWrap: "nowrap",
                            }}
                            color="text.disabled"
                          >
                            {task?.processmodelname}
                          </Typography>
                        </Box>
                      </Box>
                      <Box py={1} flex={1} overflow="hidden">
                        {task?.description && (
                          <Typography
                            // variant="h6"
                            sx={{
                              width: "100%",
                              // fontSize: "0.95rem",
                              wordBreak: "break-word",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontWeight: "bold"
                            }}
                          >
                            {task.description}
                          </Typography>
                        )}
                        <Divider
                          sx={{
                            borderColor: "lightgrey",
                            // marginBottom: "5px",
                            marginTop: "5px",
                          }}
                        />
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={task.state === 1 ? 10 : 1}
                        mb={task.state === 1 ? 1 : 2}
                      >
                        {(() => {
                          const isDoing = task.state === 1;

                          if (isDoing && loggedInUser) {
                            return null;
                          }
                          const participants = getParticipants(task.jsonparticipant);
                          if (participants.length === 0) {
                            return <AvatarRem sx={{ height: 30, width: 30 }} />;
                          }
                          const allParticipants = [...participants];
                          const tooltipTitle = allParticipants.map(p => getDisplayName(p)).join(', ');

                          return (
                            <Tooltip title={tooltipTitle}>
                              <span>
                                <AvatarGroup
                                  max={5}
                                  sx={{
                                    "& .MuiAvatar-root": {
                                      width: 30,
                                      height: 30,
                                      fontSize: "0.8rem",
                                    },
                                    justifyContent: "flex-start",
                                  }}
                                >
                                  {allParticipants.map((p) => {
                                    let iconUrl = `/users/${p.idparticipant}/picture`;

                                    if (p.participanttype === 1) {
                                      iconUrl = `/downloads/groups/${p.idparticipant}`;
                                    } else if (p.participanttype === 2) {
                                      iconUrl = `/downloads/roles/${p.idparticipant}`;
                                    }
                                    return (
                                      <AvatarRem
                                        key={p.idparticipant}
                                        icon={iconUrl}
                                      >
                                        {getInitials(p)}
                                      </AvatarRem>
                                    );
                                  })}
                                </AvatarGroup>
                              </span>
                            </Tooltip>
                          );
                        })()}
                        <Box sx={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          display: "flex",
                          gap: 0.5,
                          alignItems: "center",
                        }} >
                          {task.assignmentmode === 2 && task.iduser === 0 && (
                            <Tooltip
                              title={getTranslation(
                                translations,
                                "generic.assign_task_to_user"
                              )}
                            >
                              <IconButton
                                id="hod-dialog-button"
                                size="small"
                                onClick={handleAssignClick}
                                sx={{ mt: task.state === 1 ? 10 : 1, mb: task.state === 1 ? 1 : 2, mr: -1 }}
                              >
                                <PersonAddIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {icons.map((tooltip, index) => (
                            <Tooltip
                              key={index}
                              title={tooltip.title}
                              arrow
                              placement="top"
                            >
                              <IconButton
                                id={`task-detail-btn-${tooltip.title}`}
                                aria-label={`Add Box (${tooltip.title})`}
                                onClick={onButtonClickIcon}
                              >
                                <Badge
                                  badgeContent={[
                                    task.countcomments,
                                    task.countuserdatafields,
                                    task.countattachments,
                                  ][index]}
                                  color="primary"
                                >
                                  <tooltip.IconComponent />
                                </Badge>
                              </IconButton>
                            </Tooltip>
                          ))}
                          <TaskQuickSwapButtons
                            task={task}
                            onBack={handleBackStep}
                            onNext={canHandleNextStep ? handleNextStep : undefined}
                          />
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </>
              )}
            </CardContent>
          </Stack>
        </Stack>
      </Card>
      <HodDialogs
        isDialogOpen={isDialogOpen}
        setDialogOpen={setDialogOpen}
        loadingUsers={loadingUsers}
        groupUsers={groupUsers}
        handleUserSelect={handleUserSelect}
        isConfirmDialogOpen={isConfirmDialogOpen}
        setConfirmDialogOpen={setConfirmDialogOpen}
        selectedUser={selectedUser}
        handleConfirmAssign={handleConfirmAssign}
      />
      <SequenceFlowsDialog
        gatewayQuestion={task.flownodename}
        open={openSequenceDialog}
        onClose={() => setOpenSequenceDialog(false)}
        sequenceFlows={task.sequenceflows ?? []}
        onSelect={(sequenceFlow) => {
          handleSequenceFlowChoice?.(task, sequenceFlow);
          setOpenSequenceDialog(false);
        }}
      />
    </React.Fragment>
  );
};

export default TaskCard;
